import { Count } from "@/app/collections/view/count"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import {
  type DropPhase,
  DropPhaseType,
  type MinimalDrop,
} from "@anaconda/core/drop/types"
import { Trans } from "@lingui/react/macro"
import { Skeleton } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { DropMoleculeReatom } from "../store/drop-store.reatom"
import { ProgressBar } from "./progress-bar"

export const MintInfo = reatomComponent<{
  drop: MinimalDrop
  phaseType: DropPhase["type"]
  maxMintPerWallet: DropPhase["maxMintPerWallet"]
}>(({ drop, phaseType, maxMintPerWallet }) => {
  const {
    mintedCountAtom,
    hasMultiplePhasesAtom,
    isLoadingUserEligibilityAtom,
    isLoadingMintedAtom,
    isLoadingMintedPerPhaseAtom,
    mintedPerPhaseCountAtom,
    currentPhaseAtom,
  } = useMolecule(DropMoleculeReatom)

  const mintedCount = mintedCountAtom()
  const mintedPerPhaseCount = mintedPerPhaseCountAtom()
  const isLoadingMintedPerPhase = isLoadingMintedPerPhaseAtom()
  const isLoadingMinted = isLoadingMintedAtom()
  const hasPhases = hasMultiplePhasesAtom()
  const isLoadingUserEligibility = isLoadingUserEligibilityAtom()
  const currentPhase = currentPhaseAtom()

  if (isLoadingUserEligibility || isLoadingMinted || isLoadingMintedPerPhase)
    return <Skeleton className="h-5 w-[200px]" />

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-nowrap">
        <span className="text-foreground">
          <TotalMintedMessage
            mintedCount={mintedPerPhaseCount}
            totalSupply={drop.totalSupply}
            maxClaimableSupply={currentPhase?.maxClaimableSupply}
            hasPhases={hasPhases}
          />
        </span>
        <span className="text-foreground/60">
          <UserMintedMessage
            maxMintPerWallet={maxMintPerWallet}
            drop={drop}
            phaseType={phaseType}
          />
        </span>
      </div>
      {!hasPhases && <ProgressBar drop={drop} mintedCount={mintedCount} />}
    </div>
  )
}, "MintInfo")

function TotalMintedMessage({
  mintedCount = 0,
  totalSupply,
  maxClaimableSupply,
  hasPhases,
}: {
  mintedCount?: number
  totalSupply?: number
  maxClaimableSupply?: number
  hasPhases: boolean
}) {
  if (!totalSupply) {
    return mintedCount ? (
      <Trans>
        <Count value={mintedCount} /> minted
      </Trans>
    ) : (
      <Trans>Open Edition</Trans>
    )
  }

  return mintedCount !== undefined ? (
    <>
      <Count value={mintedCount} />
      {maxClaimableSupply && (
        <>
          {" / "}
          <Count value={maxClaimableSupply} />
        </>
      )}
      {!maxClaimableSupply && !hasPhases && (
        <>
          {" / "}
          <Count value={totalSupply} />
        </>
      )}{" "}
      <Trans>minted</Trans>
    </>
  ) : (
    <Trans>
      <Count value={mintedCount} /> items
    </Trans>
  )
}

const UserMintedMessage = reatomComponent<{
  drop: MinimalDrop
  phaseType: DropPhase["type"]
  maxMintPerWallet: DropPhase["maxMintPerWallet"]
}>(({ drop, phaseType, maxMintPerWallet }) => {
  const { myMintsPerPhaseAtom, userClaimLimitInfoAtom, isUserEligibleAtom } =
    useMolecule(DropMoleculeReatom)

  const userMintedCount = myMintsPerPhaseAtom()
  const userClaimLimitInfo = userClaimLimitInfoAtom()
  const isUserEligible = isUserEligibleAtom()

  const { isConnected } = useWalletConnect()

  if (!isConnected) {
    return null
  }

  if (!isUserEligible) {
    return null
  }

  if (phaseType === DropPhaseType.Allowlist) {
    if (userClaimLimitInfo) {
      const { alreadyMinted, limitPerWallet } = userClaimLimitInfo

      if (limitPerWallet === 0) {
        return null
      }

      return (
        <Trans>
          <span data-testid="user-minted-count">
            <Count value={alreadyMinted} />
          </span>{" "}
          / <Count value={limitPerWallet} /> minted by you
        </Trans>
      )
    }
  }

  if (userMintedCount) {
    return maxMintPerWallet !== undefined ? (
      <Trans>
        <span data-testid="user-minted-count">
          <Count value={userMintedCount} />
        </span>{" "}
        / <Count value={maxMintPerWallet} /> minted by you
      </Trans>
    ) : (
      <Trans>
        <span data-testid="user-minted-count">
          <Count value={userMintedCount} />
        </span>{" "}
        minted by you
      </Trans>
    )
  }

  return maxMintPerWallet !== undefined ? (
    <Trans>
      <Count value={maxMintPerWallet} /> per wallet
    </Trans>
  ) : null
}, "UserMintedMessage")
